import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';

import { deliveryWindowOptions } from 'pages/Sites/constants/deliveryHours';
import { FRENCH_DAYS } from 'constants/dateAndTime';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { ClosingDates } from 'pages/Sites/components/ClosingDates';

import { TotemLabel } from 'components/TotemLabel';
import { SiteFragmentFragment, TotadminPermission } from 'data/__generated__';
import { DetailFormSelect } from 'components/DetailsView/DetailFormSelect';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';

import { DetailFormCheckbox } from 'components/DetailsView/DetailFormCheckbox';
import { Link } from 'react-router-dom';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

type FormData = {
    closingDatesRanges: { start: string | null; end: string | null }[];
    accessTime: number;
};

export const DeliveryInfoSection = ({ siteDetails }: { siteDetails: SiteFragmentFragment }) => {
    const { setValue, register, watch } = useFormContext<FormData>();
    const { accessTime, deliveryDays, deliveryTimeWindow } = siteDetails;

    const selectedStartHourOption = deliveryWindowOptions.find((option) => option.value === deliveryTimeWindow.start);
    const selectedStopHourOption = deliveryWindowOptions.find((option) => option.value === deliveryTimeWindow.stop);

    return (
        <SectionContainer title="Informations de livraison" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={2}>
                <SectionColumn>
                    <TotemLabel>Jours de livraison</TotemLabel>
                    <List>
                        <tbody>
                            {FRENCH_DAYS.map((day, index) => (
                                <tr key={index}>
                                    <Cell $isBold>{day}</Cell>
                                    <Cell>
                                        <DetailFormCheckbox
                                            name={`deliveryDays.${index}`}
                                            defaultChecked={deliveryDays[index]}
                                        />
                                    </Cell>
                                </tr>
                            ))}
                        </tbody>
                    </List>
                    <Link to="/delivery-planning">
                        <TotemPrimaryButton
                            $isSecondaryStyle
                            requiredPermissions={[TotadminPermission.DeliveryPlanningRead]}
                        >
                            Planifier les livraisons
                        </TotemPrimaryButton>
                    </Link>
                </SectionColumn>
                <SectionColumn>
                    <DetailFormSelect
                        label="Début de la fenêtre de livraison"
                        placeholder="Sélectionner un horaire"
                        defaultValue={selectedStartHourOption?.value}
                        options={deliveryWindowOptions}
                        name="deliveryWindowStart"
                        dataTest="site-details_window-start"
                    />
                    <DetailFormSelect
                        label="Fin de la fenêtre de livraison"
                        placeholder="Sélectionner un horaire"
                        defaultValue={selectedStopHourOption?.value}
                        options={deliveryWindowOptions}
                        name="deliveryWindowStop"
                        dataTest="site-details_window-stop"
                    />
                    <DetailFormValue
                        label="Temps d'accès au site"
                        defaultValue={accessTime}
                        type="number"
                        step="1"
                        {...register('accessTime', { valueAsNumber: true })}
                        data-test="site-details_access-time"
                    />
                    <ClosingDates
                        closingDatesRanges={watch('closingDatesRanges')}
                        setClosingDatesRanges={(newClosingDatesRanges) => {
                            setValue('closingDatesRanges', newClosingDatesRanges);
                        }}
                    />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};
const List = styled.table`
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    min-width: 200px;

    border-spacing: 0;
    border-collapse: collapse;
`;

const Cell = styled.td<{ $isBold?: boolean }>`
    text-align: left;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-spacing: 1px;
    padding-left: 10px;
    font-weight: ${({ $isBold }) => ($isBold ? 800 : 500)};
    color: ${({ theme }) => theme.textColor};
`;
